import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, map, take } from 'rxjs';
import { AppGlobalService } from '../services/app.global.service';
import { userData } from '../STORE/selectors/auth.selector';
import { PAGE_ROLES, PageRoleConfig } from './page-roles.config';
import { GlobalRole } from '../services/util.enum';

@Injectable({
  providedIn: 'root'
})
export class RoleBasedAccessGuard {
  private router = inject(Router);
  private store = inject(Store);
  private appGlobalService = inject(AppGlobalService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const user$: Observable<any> = this.store.select(userData);

    return user$.pipe(
      take(1),
      map((user: any) => {
        if (!user?.email) {
          // User not authenticated, redirect to login
          this.router.navigate(['/login']);
          return false;
        }

        // Validate organization access from URL
        const orgValidationResult = this.validateOrgAccess(user, state);
        if (orgValidationResult.redirectUrl) {
          // Redirect to valid org URL
          this.router.navigate([orgValidationResult.redirectUrl]);
          return false;
        }

        // Get the page path from route
        const pagePath = route.routeConfig?.path || '';
        const pageConfig = PAGE_ROLES[pagePath];

        // If no configuration exists for this page, allow access (fallback behavior)
        if (!pageConfig) {
          return true;
        }

        // Check if user has access based on their role
        const hasAccess = this.checkUserAccess(user, pageConfig);

        if (!hasAccess) {
          // Redirect to app management (default page) if access denied
          const orgId = this.appGlobalService.get('orgId') || user?.orgIds?.[0];
          this.router.navigate([`/home/${orgId}/appId/settings/appmanagement`]);
          return false;
        }

        return true;
      })
    );
  }

  /**
   * Validates that user has access to the organization in the URL.
   * Admin users (devAdmin, sysAdmin, devViewer) can access any organization.
   * Regular users can only access organizations they belong to.
   * @returns Object with isValid flag and optional redirectUrl if org access is invalid
   */
  private validateOrgAccess(user: any, state: RouterStateSnapshot): { isValid: boolean; redirectUrl?: string } {
    const urlParams = state.url.split('/');
    const routerOrgId = urlParams[2] || '';
    const routerAppId = urlParams[3] || '';

    // Admin users (devAdmin, sysAdmin, devViewer) can access any organization
    const isAdminUser = user.devAdmin || user.sysAdmin || user.devViewer;

    const userOrgIds = user.orgIds || [];
    const validOrgId = user?.orgId || user?.orgIds?.[0];

    // Non-admin users must have valid org membership to proceed
    if (!isAdminUser) {
      const userOrgInfos = user?.orgInfos || {};
      if (Object.keys(userOrgInfos).length === 0) {
        console.warn(`[RoleBasedAccessGuard] Non-admin user has no orgInfos | User: ${user?.email}`);
        return { isValid: false, redirectUrl: '/' };
      }

      if (!validOrgId) {
        console.warn(`[RoleBasedAccessGuard] No valid orgId found | User: ${user?.email}`);
        return { isValid: false, redirectUrl: '/' };
      }
    }

    if (!routerOrgId || !routerOrgId.includes('-')) {
      // Invalid org format - redirect to user's org
      const fallbackOrgId = validOrgId || userOrgIds[0];
      if (!fallbackOrgId) {
        return { isValid: false, redirectUrl: '/' };
      }
      this.appGlobalService.write('orgId', fallbackOrgId);
      this.appGlobalService.write('invalidUrl', 'true');
      this.appGlobalService.write('appId', routerAppId);
      const redirectUrl = `/home/${fallbackOrgId}/appId/settings/appmanagement`;
      return { isValid: false, redirectUrl };
    }

    // Validate that user belongs to the organization in the URL (or is an admin)
    const isValidOrg = isAdminUser || userOrgIds.includes(routerOrgId);

    if (isValidOrg) {
      this.appGlobalService.write('orgId', routerOrgId);
      this.appGlobalService.write('invalidUrl', 'false');
    } else {
      // User doesn't belong to the URL org - redirect to user's org
      const fallbackOrgId = validOrgId || userOrgIds[0];
      this.appGlobalService.write('orgId', fallbackOrgId);
      this.appGlobalService.write('invalidUrl', 'true');
      const redirectUrl = `/home/${fallbackOrgId}/appId/settings/appmanagement`;
      this.appGlobalService.write('appId', 'appId');
      this.appGlobalService.write('appRole', localStorage.getItem('appRole'));
      return { isValid: false, redirectUrl };
    }

    this.appGlobalService.write('appId', routerAppId);
    this.appGlobalService.write('appRole', localStorage.getItem('appRole'));

    return { isValid: true };
  }

  private checkUserAccess(user: any, config: PageRoleConfig): boolean {
    const allowedRoles = config.allowedRoles;

    // Check global roles (user properties)
    if (user.devAdmin && allowedRoles.includes(GlobalRole.DEV_ADMIN)) {
      return true;
    }
    if (user.sysAdmin && allowedRoles.includes(GlobalRole.SYS_ADMIN)) {
      return true;
    }
    if (user.devViewer && allowedRoles.includes(GlobalRole.DEV_VIEWER)) {
      return true;
    }

    // Check org-level role (from user.orgInfos)
    const orgId = this.appGlobalService.get('orgId');
    const orgRole = user.orgInfos?.[orgId];
    if (orgRole && allowedRoles.includes(orgRole)) {
      return true;
    }

    return false;
  }
}
